import { useIsMutating } from '@tanstack/react-query';
import every from 'lodash/every';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { TDR, resolveQuantity } from 'tdr-common';
import { MUTATION_KEY as releaseSessionKey } from '../../../api/releaseCheckoutSession';
import { MUTATION_KEY as updateCheckoutItemsKey, useUpdateCheckoutItems } from '../../../api/updateCheckoutItems';
import { CheckoutCTALabel, getCheckoutCTALabel, useCheckoutContext } from '../../../context/new/CheckoutContext';
import { CheckoutGuardOutletContext } from '../../../routes/CheckoutGuard';
import { CTAButton } from '../../buttons';
import { Notice } from '../../notice-new/Notice';
import { AddOnItem } from '../AddOnItem';
import UpdateCheckoutItemsInput = TDR.CheckoutSession.UpdateCheckoutItemsInput;
import { analytics } from '../../../helpers/analytics';
import { resolveLocalizedText } from '../../../helpers/resolveLocalizedText';
import { useTranslation } from 'react-i18next';
import { ECOMMERCE_SECTION_VIEWED, CTA_CLICKED_EVENT } from '../../../helpers/analytics/constants';
import { useGTMAnalytics } from '../../../helpers/gtm/surface/useGTMAnalytics';
import { mergeAddOnItemsDTO, syncOptionalSectionSelection } from './ecommerceSelectionHelpers';

export type EcommerceOptionalItemDTO = {
	id: string;
	quantity: number;
	variantId: string;
};

type OptionalSelectionState = 'skip_selection' | 'proceed';

interface Props {
	sectionData: TDR.Ecommerce.EcommerceSection;
}

export const EcommerceOptionalSection = ({ sectionData }: Props) => {
	const { t, i18n } = useTranslation();
	const { navConfig, updateCheckoutState, checkoutState } = useCheckoutContext();
	const outletContext = useOutletContext<CheckoutGuardOutletContext>();
	const { checkoutSession, restaurant } = outletContext;
	const gtm = useGTMAnalytics();

	const [selectionState, setSelectionState] = useState<OptionalSelectionState>('skip_selection');
	const [selectedItems, setSelectedItems] = useState<EcommerceOptionalItemDTO[]>([]);

	const navigate = useNavigate();

	const { mutateAsync: updateCheckoutItems } = useUpdateCheckoutItems();

	const isUpdating = useIsMutating({
		mutationKey: [updateCheckoutItemsKey],
		exact: true
	});
	const isReleasing = useIsMutating({
		mutationKey: [releaseSessionKey],
		exact: true
	});

	useEffect(() => {
		const { selectedItems: syncedItems, selectionState: syncedState } =
			syncOptionalSectionSelection(checkoutSession.items, sectionData.items);
		setSelectedItems(syncedItems);
		setSelectionState(syncedState);
	}, [checkoutSession.items, sectionData.items]);

	useEffect(() => {
		const ctaText = resolveLocalizedText(sectionData.cta, i18n.language);
		const items = sectionData.items.map(item => ({
			id: item.id,
			name: item.name,
			price: item.price,
			currency: item.currency || 'USD'
		}));

		analytics?.track(ECOMMERCE_SECTION_VIEWED, {
			title: sectionData.title,
			ctaText,
			numberOfItems: sectionData.items.length,
			items: JSON.stringify(items),
			checkoutPosition: sectionData.checkoutPosition,
			validationType: sectionData.validation?.type || 'open',
			reservationId: checkoutSession.id
		});
	}, [sectionData, i18n.language, checkoutSession.id]);

	const ctaLabel: Record<OptionalSelectionState, CheckoutCTALabel> = {
		skip_selection: CheckoutCTALabel.SKIP,
		proceed: CheckoutCTALabel.PROCEED
	};

	const areItemsEmpty = (items: EcommerceOptionalItemDTO[]) => {
		return items?.length === 0 || every(items, (item: EcommerceOptionalItemDTO) => item.quantity === 0);
	};

	const isValidSelection = useCallback((items: EcommerceOptionalItemDTO[]) => {
		const validation = sectionData.validation;
		const nonEmptyItems = items.filter(item => item.quantity > 0);
		const totalQuantity = nonEmptyItems.reduce((sum, item) => sum + item.quantity, 0);
		const groupSize = checkoutSession.guests;

		switch (validation.type) {
			case TDR.Ecommerce.ValidationType.Open:
				return true;

			case TDR.Ecommerce.ValidationType.SingleMandatory:
				if (nonEmptyItems.length !== 1) {
					return false;
				}
				const selectedItem = nonEmptyItems[0];
				const addonItem = sectionData.items.find(i => i.variantId === selectedItem.variantId);
				const effectiveQuantityMode = addonItem?.quantityMode ||
				(validation.minQuantity === TDR.Ecommerce.QuantityMode.GROUP_SIZE ? TDR.Ecommerce.QuantityMode.GROUP_SIZE : TDR.Ecommerce.QuantityMode.SINGLE);
				const expectedQuantity = effectiveQuantityMode === TDR.Ecommerce.QuantityMode.GROUP_SIZE
					? (groupSize || 1)
					: 1;
				return selectedItem.quantity === expectedQuantity;

			case TDR.Ecommerce.ValidationType.MixedMandatory:
				if (nonEmptyItems.length === 0) {
					return false;
				}
				const minQuantity = resolveQuantity(validation.minQuantity, groupSize, 1);
				const maxQuantity = validation.maxQuantity ? resolveQuantity(validation.maxQuantity, groupSize, Infinity) : undefined;

				if (totalQuantity < minQuantity) {
					return false;
				}
				if (maxQuantity && totalQuantity > maxQuantity) {
					return false;
				}

				return true;

			default:
				return true;
		}
	}, [sectionData.validation, checkoutSession.guests]);

	const updateItem = useCallback((id: string, quantity: number, variantId: string = id) => {
		// Check if this would exceed the max quantity for mixed-mandatory validation
		if (sectionData.validation.type === TDR.Ecommerce.ValidationType.MixedMandatory && sectionData.validation.maxQuantity) {
			const groupSize = checkoutSession.guests;
			const maxQuantity = resolveQuantity(sectionData.validation.maxQuantity, groupSize, Infinity);
			const currentTotal = selectedItems.reduce((sum, item) => sum + item.quantity, 0);
			const currentItemQuantity = selectedItems.find(item => item.id === id && item.variantId === variantId)?.quantity || 0;
			const newTotal = currentTotal - currentItemQuantity + quantity;

			if (newTotal > maxQuantity) {
				// Don't allow the update if it would exceed the max
				return;
			}
		}

		setSelectedItems(prevItems => {
			const existingItemIndex = prevItems.findIndex(item => item.id === id && item.variantId === variantId);

			if (existingItemIndex >= 0) {
				const updatedItems = [...prevItems];
				if (quantity <= 0) {
					updatedItems.splice(existingItemIndex, 1);
				}
				else {
					updatedItems[existingItemIndex] = { id, quantity, variantId };
				}
				return updatedItems;
			}
			else if (quantity > 0) {
				return [...prevItems, { id, quantity, variantId }];
			}

			return prevItems;
		});
	}, [selectedItems, sectionData.validation, checkoutSession.guests]);

	const getMaxQuantityForItem = useCallback((itemId: string, variantId: string) => {
		// Get the item data to check for per-item quantity limits
		const item = sectionData.items.find(item => item.id === itemId && item.variantId === variantId);
		const perItemLimit = item?.maxQuantityPerItem;

		// If there's a per-item limit, use the minimum of that and any validation limits
		if (perItemLimit !== undefined) {
			if (sectionData.validation.type !== TDR.Ecommerce.ValidationType.MixedMandatory || !sectionData.validation.maxQuantity) {
				return perItemLimit; // Use per-item limit if no validation limits
			}

			const groupSize = checkoutSession.guests;
			const maxQuantity = resolveQuantity(sectionData.validation.maxQuantity, groupSize, Infinity);
			const currentTotal = selectedItems.reduce((sum, item) => sum + item.quantity, 0);
			const currentItemQuantity = selectedItems.find(item => item.id === itemId && item.variantId === variantId)?.quantity || 0;

			// Return the minimum of per-item limit and remaining validation quantity
			const remainingValidationQuantity = maxQuantity - currentTotal + currentItemQuantity;
			return Math.min(perItemLimit, remainingValidationQuantity);
		}

		// Fallback to original validation logic if no per-item limit
		if (sectionData.validation.type !== TDR.Ecommerce.ValidationType.MixedMandatory || !sectionData.validation.maxQuantity) {
			return Infinity; // No limit for non-mixed-mandatory or when no max is set
		}

		const groupSize = checkoutSession.guests;
		const maxQuantity = resolveQuantity(sectionData.validation.maxQuantity, groupSize, Infinity);
		const currentTotal = selectedItems.reduce((sum, item) => sum + item.quantity, 0);
		const currentItemQuantity = selectedItems.find(item => item.id === itemId && item.variantId === variantId)?.quantity || 0;

		// Return the remaining available quantity
		return maxQuantity - currentTotal + currentItemQuantity;
	}, [selectedItems, sectionData.validation, checkoutSession.guests, sectionData.items]);

	useEffect(() => {
		const validationState = sectionData.validation.type === TDR.Ecommerce.ValidationType.Open
			? (!areItemsEmpty(selectedItems) ? 'proceed' : 'skip_selection')
			: (isValidSelection(selectedItems) ? 'proceed' : 'skip_selection');
		setSelectionState(validationState);
	}, [selectedItems, areItemsEmpty, isValidSelection, sectionData.validation.type]);

	const handleProceedToNextStep = () => {
		const itemsToSend = areItemsEmpty(selectedItems) ? [] : selectedItems;

		const currentStep = window.location.pathname.split('/').pop();

		const payload: UpdateCheckoutItemsInput = {
			checkoutId: checkoutSession.id,
			items: itemsToSend as any,
			checkoutStep: currentStep
		};

		const itemsWithDetails = itemsToSend.map(selectedItem => {
			const itemData = sectionData.items.find(item => item.id === selectedItem.id);
			return {
				id: selectedItem.id,
				name: itemData?.name || '',
				price: itemData?.price || 0,
				quantity: selectedItem.quantity,
				currency: itemData?.currency || 'USD'
			};
		});

		analytics?.track(CTA_CLICKED_EVENT, {
			purpose: 'ecommerce proceed',
			title: sectionData.title,
			numberOfItemsSelected: itemsToSend.length,
			items: JSON.stringify(itemsWithDetails),
			checkoutPosition: sectionData.checkoutPosition,
			validationType: sectionData.validation?.type || 'open',
			reservationId: checkoutSession.id
		});

		if (itemsToSend.length > 0) {
			gtm.addToCart({
				checkoutId: checkoutSession.id,
				guests: checkoutSession.guests,
				items: itemsWithDetails.map((item) => ({
					id: item.id,
					name: item.name,
					price: item.price,
					quantity: item.quantity
				}))
			});
		}

		const sectionItemIds = new Set(sectionData.items.map(item => item.id));
		updateCheckoutState({
			addOnItemsDTO: mergeAddOnItemsDTO(checkoutState.addOnItemsDTO || [], itemsToSend, sectionItemIds)
		});
		updateCheckoutItems({ input: payload })
			.then(() => {
				if (itemsToSend.length > 0) {
					analytics?.track('Optional Items Selected', {
						items: itemsToSend,
						sectionTitle: sectionData.title,
						reservationId: checkoutSession.id
					});
				}
				else {
					analytics?.track('Optional Items Skipped', {
						sectionTitle: sectionData.title,
						reservationId: checkoutSession.id
					});
				}

				const nextStep = getNextCheckoutStep();
				navigate(`../${nextStep}`);
			})
			.catch((err) => {
				console.error(err);
			});
	};

	const getNextCheckoutStep = () => {
		const pathname = window.location.pathname;
		const currentStep = pathname.split('/').pop();


		const currentNavItem = navConfig[currentStep];
		if (currentNavItem && currentNavItem.next) {
			return currentNavItem.next;
		}

		const orderedSteps = Object.values(navConfig).sort((a, b) => a.order - b.order);
		const currentIndex = orderedSteps.findIndex(step => step.path === currentStep);
		const nextStep = orderedSteps[currentIndex + 1];


		return nextStep?.path || 'policies';
	};

	const getNoticeText = () => {
		const noticeText = (sectionData as any).noticeText;
		const cta = sectionData.cta;
		return resolveLocalizedText(noticeText || cta, i18n.language);
	};

	const getButtonText = () => {
		// Check if we should show skip label (no items selected OR validation fails)
		const shouldShowSkipLabel = selectionState === 'skip_selection';

		if (shouldShowSkipLabel && (sectionData as any).skipCtaLabel) {
			return resolveLocalizedText((sectionData as any).skipCtaLabel, i18n.language);
		}

		// Check if we should show normal CTA label (items selected AND validation passes)
		if (selectionState === 'proceed' && (sectionData as any).ctaLabel) {
			return resolveLocalizedText((sectionData as any).ctaLabel, i18n.language);
		}

		// Fallback to section CTA or default labels
		if (sectionData.cta) {
			return resolveLocalizedText(sectionData.cta, i18n.language);
		}

		return getCheckoutCTALabel(ctaLabel[selectionState], t);
	};

	return (
		<>
			<div className="flex flex-col flex-1 overflow-y-auto">
				<div className="flex flex-col gap-4 p-4 pt-40 sm:pt-4">
					<Notice text={getNoticeText()} />

					{sectionData.items.map((item) => (
						<AddOnItem
							key={item.id}
							product={{
								id: item.id,
								title: item.name,
								description: item.description || '',
								price: {
									amount: item.price,
									unit: item.currency || 'USD'
								},
								variantId: item.variantId,
								imageUrl: item.imageUrl || '',
								tag: item.tag || '',
								unit: item.unit || 'unit',
								isSignatureItem: item.isSignatureItem || false,
								leadTimeHours: item.leadTimeHours
							}}
							restaurant={restaurant}
							updateAddOns={(id, quantity, variantId) => updateItem(id, quantity, variantId)}
							initialQuantity={selectedItems.find(selected => selected.id === item.id)?.quantity || 0}
							maxQuantity={getMaxQuantityForItem(item.id, item.variantId)}
						/>
					))}
				</div>
			</div>

			<div className="p-4 border-t border-gray-700">
				<CTAButton
					onClick={handleProceedToNextStep}
					buttonText={getButtonText()}
					loading={!!isUpdating}
					disabled={!!isUpdating || !!isReleasing ||
						(sectionData.validation.type !== TDR.Ecommerce.ValidationType.Open && !isValidSelection(selectedItems))}
				/>
			</div>
		</>
	);
};